/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-21 10:15:50                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-01-27 13:18:27                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * Validator
  * CustomerForm - customer edit/create form
    * handleChange: handle change event
*/

/* Imports */
import { t } from "i18next";
import { useEffect, useState } from "react";
/***/

/* Components */
import AddressSelect from "../address/AddressSelect";
import { useRef } from "react";
import ContactInfoForm, { ContactInfoFormValidator } from "../contact-infos/ContactInfoForm";
import FormDropdown from "../../form-dropdown/FormDropdown";
import Switch from "../../switch/Switch";
/***/

/* Services */
import { embeddedFormChange } from "../../../services/utils/utils";
import { validator } from "../../../services/form-validator/form-validator";
import DynamicListSelect from "../dynamic-list/DynamicListSelect";
import { api } from "../../../services/api/api";
import { CustomerValidator } from "./customer-validator";
import ReportTemplateSelect from "../report/ReportTemplateSelect";
import { isOdooConnected } from "../../../services/odoo/odoo";
import { useUserRoles } from "../../../hooks/useUserRoles";
import AutomaticReportSendSelect, {
  mapAutomaticReportSendToApi,
  resolveAutomaticReportSendMode
} from "./AutomaticReportSendSelect";
import {
  isCustomerLanguageLink,
  isLanguageRecord,
  markDefaultLanguageLabels,
  resolveCustomerLanguagesForDisplay,
  stripDefaultLanguageTag,
  toCustomerLanguagePayload
} from "./customerLanguages";
/***/

/* Validator */
export const CustomerFormValidator = {
  name: (e: unknown) => validator.isString(e),
  activitySector: (e: unknown) => validator.isNotNull(e),
  languages: (e: unknown) => {
    if (!Array.isArray(e)) return false;
    if (e.length === 0) return false;

    return e.every(lang =>
      lang &&
      typeof lang === "object" &&
      typeof lang.id === "string" &&
      lang.language &&
      typeof lang.language === "object" &&
      typeof lang.language.id === "string" &&
      (typeof lang.default === "boolean" || typeof lang.isDefault === "boolean")
    );
  },
  nip: (e: unknown) => validator.isString(e),
  badPayer: (e: unknown) => true,
  contactInfo: (e: unknown) => {
    if (!e || typeof e !== "object") return false;
    return validator.isFormValid(e, ContactInfoFormValidator);
  },
  address: (e: unknown) => validator.isNotNull(e)
};

const v = CustomerFormValidator;
/***/

/**
* CustomerForm - customer edit/create form
* @param data - customer data for edition or empty object for creation
* @param onChange - field change event
* @param onSubmit - form submit event
* @param submitted - submitted state, trigger field validation
*/
export default function CustomerForm({
  data,
  onChange,
  onSubmit,
  submitted,
  isEditing
}: {
  data?: EasyTech.Customer,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean,
  isEditing?: boolean
}) {
  const { isSuperAdmin } = useUserRoles();

  /*
   * L'identifiant Odoo n'est modifiable qu'en édition, et seulement par un super admin sur un realm
   * relié à Odoo. En création il est volontairement absent : une fiche qui vient de naître ici n'a
   * pas encore de contrepartie dans Odoo, et le bouton « Envoyer vers Odoo » de la fiche client est
   * le chemin normal pour lui en donner une.
   */
  const canEditExternalId = isEditing === true && isSuperAdmin && isOdooConnected();

  const validateFormData = (formData: any): boolean => {
    if (!formData) return false;

    // Utiliser le validateur spécialisé pour les customers
    const validationErrors = CustomerValidator.validate(formData, t);
    return validationErrors.length === 0;
  };

  // Fonction pour soumettre avec validation
  const handleSubmitWithValidation = () => {
    if (submitted && !validateFormData(data)) {
      console.warn("Validation échouée - champs obligatoires manquants");
      return;
    }
    onSubmit();
  };
  
  const [languagesForDisplay, setLanguagesForDisplay] = useState<EasyTech.Language[]>([]);
  
  useEffect(() => {
    if (!data?.languages || data.languages.length === 0) {
      setLanguagesForDisplay([]);
      return;
    }

    const firstLang = data.languages[0];

    if (isLanguageRecord(firstLang) && !isCustomerLanguageLink(firstLang)) {
      setLanguagesForDisplay(markDefaultLanguageLabels(data.languages as EasyTech.Language[]));
      return;
    }

    if (isCustomerLanguageLink(firstLang)) {
      const links = data.languages.filter(isCustomerLanguageLink);

      if (links.length === 0) {
        setLanguagesForDisplay([]);
        return;
      }

      const currentDisplayIds = languagesForDisplay
        .map((l) => l.id)
        .filter((id): id is string => typeof id === "string");
      const linkIds = links.map((l) => l.language.id);
      const defaultLinkId = links.find((l) => l.isDefault === true || l.default === true)?.language.id ?? linkIds[0];
      if (
        currentDisplayIds.length === linkIds.length &&
        currentDisplayIds[0] === defaultLinkId &&
        currentDisplayIds.every((id) => linkIds.includes(id))
      ) {
        return;
      }

      api.get("users/languages").then((response: unknown) => {
        setLanguagesForDisplay(
          resolveCustomerLanguagesForDisplay(response as EasyTech.Language[], links)
        );
      }).catch(() => {
        setLanguagesForDisplay(resolveCustomerLanguagesForDisplay([], links));
      });
    }
  }, [data?.languages]);

  const handleChange = (name: string, value: any) => {
    if (name === "languages") {
      if (!Array.isArray(value) || value.length === 0) {
        setLanguagesForDisplay([]);
        onChange(name, []);
        return;
      }

      const validLanguages = (value as EasyTech.Language[])
        .filter((lang) => lang && lang.id)
        .map((lang) => ({ ...lang, label: stripDefaultLanguageTag(lang.label || "") }));

      if (validLanguages.length === 0) {
        setLanguagesForDisplay([]);
        onChange(name, []);
        return;
      }

      // First picked language is the default; show it immediately so the tag does not wait
      // for the catalog round-trip that follows when the parent writes the link payload.
      setLanguagesForDisplay(markDefaultLanguageLabels(validLanguages));
      onChange(name, toCustomerLanguagePayload(validLanguages, data?.languages || []));
    } else {
      onChange(name, value);
    }
  };

  const changeRef = useRef(handleChange);
  changeRef.current = handleChange;
  /***/

  return(
    <>
      <div className="field">
        <label htmlFor="name">{t("common:name")} *</label>
        <input  type="text"
                id="name"
                name="name"
                className={`form-field ${(submitted && !validator.isFieldValid(data, "name", v)) && "error"}`}
                placeholder="ex: Michel SARL"
                defaultValue={data?.name}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}/>
      </div>

      {canEditExternalId && (
        <div className="field">
          <label htmlFor="externalId">{t("client:odoo:externalId:label")}</label>

          {/* value plutôt que defaultValue : vider le champ doit remonter "" jusqu'au parent, qui
              le lit comme « délier cette fiche d'Odoo ». */}
          <input  type="text"
                  id="externalId"
                  name="externalId"
                  className="form-field"
                  placeholder={t("client:odoo:externalId:placeholder")}
                  value={data?.externalId ?? ""}
                  onChange={(e) => handleChange(e.target.name, e.target.value)}
                  onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}/>

          <div className="field-hint">
            <span className="text-xs text-gray-500">{t("client:odoo:externalId:help")}</span>
          </div>
        </div>
      )}

      <div className="field">
        <label htmlFor="activity">{t("common:activitySector")} *</label>
        <DynamicListSelect  data={data?.activitySector}
                            ressource="cu-activity"
                            onChange={(e) => handleChange("activitySector", e)}
                            className={`${submitted && !validator.isFieldValid(data, "activitySector", v) && "error"}`}/>
      </div>

      <div className="field">
        <label htmlFor="languages">{t("common:languages")} *</label>
        <DynamicListSelect  data={languagesForDisplay}
                            ressource="cu-languages"
                            isMulti={true}
                            onChange={(e) => handleChange("languages", e)}
                            className={`${submitted && !validator.isFieldValid(data, "languages", v) && "error"}`}/>
      </div>

      <div className="field">
        <label htmlFor="nip">{t("common:nip")} *</label>
        <input  type="text"
                id="nip"
                name="nip"
                className={`form-field ${(submitted && !validator.isFieldValid(data, "nip", v)) && "error"}`}
                placeholder="ex: 1577847"
                defaultValue={data?.nip}
                onChange={(e) => handleChange(e.target.name, e.target.value)}
                onKeyDown={(e) => e.key === "ENTER" && handleSubmitWithValidation()}/>
      </div>

      <div className="field">
        <label htmlFor="badPayer">{t("common:badPayer")}</label>
        <div className="flex items-center gap-2">
          <Switch checked={data?.badPayer || false}
                  onChange={(checked) => handleChange("badPayer", checked)}/>
          <span className="text-sm text-gray-600">{t("client:form:fields:badPayer:markAs")}</span>
        </div>
      </div>

      <div className="field">
        <label htmlFor="purchaseOrderMandatory">{t("client:form:fields:purchaseOrderMandatory:label")}</label>
        <div className="flex items-center gap-2">
          <Switch checked={data?.purchaseOrderMandatory || false}
                  onChange={(checked) => handleChange("purchaseOrderMandatory", checked)}/>
          <span className="text-sm text-gray-600">{t("client:form:fields:purchaseOrderMandatory:markAs")}</span>
        </div>
      </div>

      {/* hideNotes: the general note is edited in the Notes group below, next to the two
          role-addressed ones, rather than at the end of the contact block. */}
      <ContactInfoForm  data={data?.contactInfo || {email: "", phone: "", fax: "", notes: ""}}
                        hideNotes
                        onChange={(name, value) =>
                          embeddedFormChange(data || {}, "contactInfo", name, value, handleChange)
                        }
                        onSubmit={handleSubmitWithValidation}
                        submitted={submitted}/>

      <AddressSelect  label={`${t("common:address")} *`}
                      value={data?.address}
                      className={`form-field ${(submitted && !validator.isFieldValid(data, "address", v)) && "error"}`}
                      onChange={(value) => changeRef.current("address", value)}/>

      {/*
        * The three notes in one group, in the same order as the customer profile and the
        * intervention detail: general, then technician, then planner. They used to be split -- the
        * general one at the end of the contact block, the other two after the address -- so editing
        * "the notes" meant hunting through the form.
        *
        * All three are optional and deliberately absent from CustomerFormValidator and
        * CustomerValidator: a missing note must never block saving a customer.
        *
        * The helper line names the audience and where the note will show up -- without it, "planner
        * note" and "technician note" get filled with each other's content. It carries field-hint so
        * it lands under the textarea it describes: the drawer lays a field out as two columns, and a
        * third child with no placement falls back to column one, under the label.
        *
        * value (not defaultValue) so clearing a field reaches onChange as "", which is what core
        * reads as "clear this note". An untouched field keeps whatever was loaded, and a never-set
        * note stays undefined and is simply omitted from the PUT.
        */}
      <FormDropdown label={t("client:notes:title")}>
        <div className="field">
          <label htmlFor="notes">{t("client:notes:general")}</label>
          <textarea id="notes"
                    name="notes"
                    placeholder={t("client:notes:placeholder")}
                    value={data?.notes || ""}
                    onChange={(e) => handleChange(e.target.name, e.target.value)}/>
        </div>

        <div className="field">
          <label htmlFor="technicianNote">{t("client:form:fields:technicianNote:label")}</label>
          <textarea id="technicianNote"
                    name="technicianNote"
                    maxLength={1000}
                    placeholder={t("client:form:fields:technicianNote:placeholder")}
                    value={data?.technicianNote || ""}
                    onChange={(e) => handleChange(e.target.name, e.target.value)}/>

          <div className="field-hint flex justify-between items-center mt-1">
            <span className="text-xs text-gray-500">{t("client:form:fields:technicianNote:help")}</span>
            <span className="text-xs text-gray-500">{(data?.technicianNote?.length || 0)}/1000</span>
          </div>
        </div>

        <div className="field">
          <label htmlFor="plannerNote">{t("client:form:fields:plannerNote:label")}</label>
          <textarea id="plannerNote"
                    name="plannerNote"
                    maxLength={1000}
                    placeholder={t("client:form:fields:plannerNote:placeholder")}
                    value={data?.plannerNote || ""}
                    onChange={(e) => handleChange(e.target.name, e.target.value)}/>

          <div className="field-hint flex justify-between items-center mt-1">
            <span className="text-xs text-gray-500">{t("client:form:fields:plannerNote:help")}</span>
            <span className="text-xs text-gray-500">{(data?.plannerNote?.length || 0)}/1000</span>
          </div>
        </div>
      </FormDropdown>

      <div className="field">
        <label htmlFor="reportTemplate">{t("client:form:fields:reportTemplate:label")}</label>
        <ReportTemplateSelect
          value={data?.reportTemplate ?? null}
          onChange={(template) => handleChange("reportTemplate", template)}
        />
      </div>

      <div className="field">
        <label htmlFor="automaticReportSend">{t("client:form:fields:automaticReportSend:label")}</label>
        <AutomaticReportSendSelect
          value={resolveAutomaticReportSendMode(data?.sendInterventionReportEmail)}
          onChange={(mode) => handleChange("sendInterventionReportEmail", mapAutomaticReportSendToApi(mode))}
        />
      </div>
    </>
  );
}
/***/
